import { paidUnitGr, type Id } from '@odhaczka/core';
import { useLiveQuery } from 'dexie-react-hooks';
import { db } from '../db/db';
import type { ArticleRow, ProductRow, ReceiptLineRow } from '../db/types';
import { buildMergeMap, resolveWith } from './products';

// Historia cen z paragonów: zakupy artykułu albo wszystkich artykułów produktu.

export interface Purchase {
  lineId: Id;
  receiptId: Id;
  articleId: Id;
  ms: number;
  qty: number;
  weight: boolean;
  /** Cena półkowa za sztukę albo kg. */
  shelfGr: number;
  /** Zapłacona cena za sztukę albo kg (po rabatach). */
  paidGr: number;
  /** Zapłacono za całą linię. */
  totalGr: number;
  discountGr: number;
  discounts: string | null;
}

export interface History {
  product: ProductRow | null;
  articles: ArticleRow[];
  /** Najnowsze pierwsze. */
  purchases: Purchase[];
}

async function purchasesOf(articleIds: Id[]): Promise<Purchase[]> {
  if (articleIds.length === 0) return [];
  const lines = (await db.receiptLines.where('articleId').anyOf(articleIds).toArray()).filter((l) => !l.deleted);
  const receipts = new Map((await db.receipts.bulkGet([...new Set(lines.map((l) => l.receiptId))])).filter((r) => !!r && !r.deleted).map((r) => [r!.id, r!]));
  const out: Purchase[] = [];
  for (const l of lines) {
    const r = receipts.get(l.receiptId);
    if (!r?.purchasedMs) continue;
    out.push(toPurchase(l, r.purchasedMs));
  }
  return out.sort((a, b) => b.ms - a.ms);
}

function toPurchase(l: ReceiptLineRow, ms: number): Purchase {
  const qty = l.qty ?? 1;
  const valueGr = l.valueGr ?? 0;
  const discountGr = l.discountGr ?? 0;
  return {
    lineId: l.id,
    receiptId: l.receiptId,
    articleId: l.articleId ?? '',
    ms,
    qty,
    weight: !!l.weight,
    shelfGr: l.unitGr ?? 0,
    paidGr: paidUnitGr({ qty, valueGr, discountGr }),
    totalGr: valueGr - discountGr,
    discountGr,
    discounts: l.discounts,
  };
}

export function useProductHistory(productId: Id | undefined): History | null | undefined {
  return useLiveQuery(async () => {
    if (!productId) return null;
    const products = await db.products.toArray();
    const resolve = resolveWith(buildMergeMap(products));
    const id = resolve(productId);
    const product = products.find((p) => p.id === id) ?? null;
    if (!product || product.deleted) return null;
    const articles = (await db.articles.where('productId').anyOf(products.filter((p) => resolve(p.id) === id).map((p) => p.id)).toArray())
      .filter((a) => !a.deleted)
      .sort((a, b) => (b.lastBoughtMs ?? 0) - (a.lastBoughtMs ?? 0));
    return { product, articles, purchases: await purchasesOf(articles.map((a) => a.id)) };
  }, [productId]);
}

export function useArticleHistory(articleId: Id | undefined): History | null | undefined {
  return useLiveQuery(async () => {
    if (!articleId) return null;
    const article = await db.articles.get(articleId);
    if (!article || article.deleted) return null;
    let product: ProductRow | null = null;
    if (article.productId) {
      const products = await db.products.toArray();
      const id = resolveWith(buildMergeMap(products))(article.productId);
      product = products.find((p) => p.id === id && !p.deleted) ?? null;
    }
    return { product, articles: [article], purchases: await purchasesOf([article.id]) };
  }, [articleId]);
}

export interface PriceStats {
  last: Purchase | null;
  /** Najniższa cena półkowa w ostatnich 12 miesiącach. */
  minShelfGr: number | null;
  /** Zwykła (mediana) cena półkowa w ostatnich 12 miesiącach. */
  typicalShelfGr: number | null;
  /** Ostatni zakup z rabatem. */
  lastPromo: Purchase | null;
  count12: number;
  spent12Gr: number;
}

const YEAR_MS = 365 * 86_400_000;

export function priceStats(purchases: readonly Purchase[], now = Date.now()): PriceStats {
  const year = purchases.filter((p) => p.ms >= now - YEAR_MS);
  const shelves = year.map((p) => p.shelfGr).sort((a, b) => a - b);
  const mid = shelves.length >> 1;
  return {
    last: purchases[0] ?? null,
    minShelfGr: shelves[0] ?? null,
    typicalShelfGr: shelves.length === 0 ? null : shelves.length % 2 ? shelves[mid]! : Math.round((shelves[mid - 1]! + shelves[mid]!) / 2),
    lastPromo: purchases.find((p) => p.discountGr > 0) ?? null,
    count12: year.length,
    spent12Gr: year.reduce((s, p) => s + p.totalGr, 0),
  };
}
